import {stageDisplayName} from '../journal/stage.js';

export const LiveTimelineType=Object.freeze({
  STAGE_START:'stage_start',
  STAGE_END:'stage_end',
  TEMPERATURE:'temperature',
  EVENT:'event',
  NOTE:'note',
  PHOTO:'photo'
});

const TYPE_ORDER=Object.freeze({
  [LiveTimelineType.STAGE_END]:10,
  [LiveTimelineType.STAGE_START]:20,
  [LiveTimelineType.TEMPERATURE]:30,
  [LiveTimelineType.EVENT]:40,
  [LiveTimelineType.NOTE]:50,
  [LiveTimelineType.PHOTO]:60
});

function rows(value){
  return Array.isArray(value)?value:[];
}

function validTimestamp(value){
  const text=String(value||'').trim();
  return text&&!Number.isNaN(Date.parse(text))?text:null;
}

function number(value){
  const n=Number(value);
  return Number.isFinite(n)?n:null;
}

function temperature(value){
  const n=number(value);
  if(n==null)return null;
  return n.toFixed(Number.isInteger(n)?0:1)+'°C';
}

function active(value=[]){
  return rows(value).filter(row=>!row?.deletedAt);
}

function byOrder(a,b){
  return Number(a?.sortOrder||0)-Number(b?.sortOrder||0)
    ||String(a?.id||'').localeCompare(String(b?.id||''));
}

function pending(row){
  const sync=String(row?.syncStatus||'');
  const photo=String(row?.photoStatus||'');
  return sync.startsWith('pending_')
    ||['pending_upload','upload_failed','pending_metadata','metadata_failed'].includes(photo);
}

function entry({
  key,
  type,
  timestamp,
  label,
  detail=null,
  sourceId,
  stageId=null,
  itemId=null,
  pending=false,
  meta={}
}){
  const at=validTimestamp(timestamp);
  if(!at||!key||!type||!sourceId)return null;
  return Object.freeze({
    key:String(key),
    type:String(type),
    timestamp:at,
    label:String(label||'Zapis Live'),
    detail:detail==null||detail===''?null:String(detail),
    sourceId:String(sourceId),
    stageId:stageId||null,
    itemId:itemId||null,
    pending:Boolean(pending),
    meta:Object.freeze({...meta})
  });
}

function transitionReasonDetail(transition,stageName){
  const reason=String(transition?.reason||'');
  if(reason==='skip'){
    const names=rows(transition?.skippedStageIds).map(stageName).filter(Boolean);
    return names.length?'Pominięto: '+names.join(', '):'Pominięto zaplanowane etapy';
  }
  if(reason==='reopen')return 'Ponownie otwarto wcześniejszy etap';
  if(reason==='custom_insert')return 'Dodano własny etap podczas Live';
  return null;
}

function eventLabel(event,{itemName,woodName,stageName}){
  switch(String(event?.eventType||'')){
    case 'wood_added': return 'Dodano drewno'+(event.woodId?': '+(woodName(event.woodId)||'drewno'):'');
    case 'chamber_opened': return 'Otwarto komorę';
    case 'product_checked': return 'Sprawdzono produkt'+(event.itemId?': '+(itemName(event.itemId)||'produkt'):'');
    case 'stage_changed': return 'Zmiana etapu'+(event.stageId?': '+(stageName(event.stageId)||'etap'):'');
    case 'custom': return String(event.customText||'Inne zdarzenie');
    default: return 'Zdarzenie Live';
  }
}

function timelineEntries(bundle={}){
  const session=bundle.session||{};
  const stages=active(bundle.stages).sort(byOrder);
  const items=active(bundle.items).sort(byOrder);
  const woods=active(bundle.woods).sort(byOrder);
  const stageMap=new Map(stages.map(stage=>[
    String(stage.id),
    stageDisplayName(stage,{locale:'pl'})
  ]));
  const itemMap=new Map(items.map(item=>[String(item.id),String(item.nameSnapshot||'Produkt')]));
  const woodMap=new Map(woods.map(wood=>[String(wood.id),String(wood.nameSnapshot||'Drewno')]));
  const stageName=id=>stageMap.get(String(id||''))||null;
  const itemName=id=>itemMap.get(String(id||''))||null;
  const woodName=id=>woodMap.get(String(id||''))||null;

  const transitions=[...rows(bundle.liveStageTransitions)]
    .filter(row=>row?.syncStatus!=='conflict')
    .filter(row=>validTimestamp(row?.transitionedAt))
    .sort((a,b)=>Date.parse(a.transitionedAt)-Date.parse(b.transitionedAt)
      ||String(a?.id||'').localeCompare(String(b?.id||'')));

  const result=[];
  const actualStartAt=validTimestamp(session.actualStartAt);
  if(actualStartAt){
    const firstStageId=transitions[0]?.fromStageId||session.activeStageId||null;
    if(firstStageId){
      result.push(entry({
        key:'session-stage-start:'+String(session.id||'session')+':'+String(firstStageId),
        type:LiveTimelineType.STAGE_START,
        timestamp:actualStartAt,
        label:'Start etapu: '+(stageName(firstStageId)||'Etap'),
        sourceId:String(session.id||'session'),
        stageId:firstStageId,
        meta:{initial:true}
      }));
    }
  }

  const transitionFingerprints=new Set();
  for(const transition of transitions){
    const timestamp=validTimestamp(transition.transitionedAt);
    if(!timestamp)continue;
    const id=String(transition.id||'');
    if(!id)continue;
    const fromName=stageName(transition.fromStageId)||'Etap';
    const toName=stageName(transition.toStageId)||'Etap';
    const detail=transitionReasonDetail(transition,stageName);
    transitionFingerprints.add(Date.parse(timestamp)+'|'+String(transition.toStageId||''));

    result.push(entry({
      key:'stage-transition:'+id+':end',
      type:LiveTimelineType.STAGE_END,
      timestamp,
      label:'Koniec etapu: '+fromName,
      detail,
      sourceId:id,
      stageId:transition.fromStageId,
      pending:pending(transition),
      meta:{reason:transition.reason||null,transitionPart:'end'}
    }));
    result.push(entry({
      key:'stage-transition:'+id+':start',
      type:LiveTimelineType.STAGE_START,
      timestamp,
      label:'Start etapu: '+toName,
      detail:transition.reason==='reopen'||transition.reason==='custom_insert'?detail:null,
      sourceId:id,
      stageId:transition.toStageId,
      pending:pending(transition),
      meta:{reason:transition.reason||null,transitionPart:'start'}
    }));
  }

  for(const reading of rows(bundle.temperatureReadings)){
    const timestamp=validTimestamp(reading?.recordedAt);
    const id=String(reading?.id||'');
    if(!timestamp||!id)continue;
    const value=temperature(reading.valueCelsius);
    const internal=String(reading.readingKind||'')==='internal';
    const scope=internal
      ?(reading.itemId?itemName(reading.itemId)||'Produkt':'Produkt')
      :'Komora';
    const correction=reading.correctsReadingId
      ?'Korekta'+(reading.correctionNote?': '+reading.correctionNote:'')
      :null;
    result.push(entry({
      key:'temperature:'+id,
      type:LiveTimelineType.TEMPERATURE,
      timestamp,
      label:scope+(value?' · '+value:''),
      detail:correction,
      sourceId:id,
      itemId:reading.itemId||null,
      pending:pending(reading),
      meta:{
        readingKind:reading.readingKind||null,
        valueCelsius:number(reading.valueCelsius),
        correction:Boolean(reading.correctsReadingId)
      }
    }));
  }

  for(const event of rows(bundle.liveEvents)){
    if(event?.syncStatus==='conflict'&&String(event?.eventType||'')==='stage_changed')continue;
    const timestamp=validTimestamp(event?.occurredAt);
    const id=String(event?.id||'');
    if(!timestamp||!id)continue;
    if(String(event.eventType||'')==='stage_changed'){
      const fingerprint=Date.parse(timestamp)+'|'+String(event.stageId||'');
      if(transitionFingerprints.has(fingerprint))continue;
    }
    result.push(entry({
      key:'event:'+id,
      type:LiveTimelineType.EVENT,
      timestamp,
      label:eventLabel(event,{itemName,woodName,stageName}),
      detail:event.note||null,
      sourceId:id,
      stageId:event.stageId||null,
      itemId:event.itemId||null,
      pending:pending(event),
      meta:{eventType:event.eventType||null,legacyStageChange:event.eventType==='stage_changed'}
    }));
  }

  for(const note of rows(bundle.liveNotes)){
    const timestamp=validTimestamp(note?.capturedAt);
    const id=String(note?.id||'');
    if(!timestamp||!id)continue;
    result.push(entry({
      key:'note:'+id,
      type:LiveTimelineType.NOTE,
      timestamp,
      label:'Notatka',
      detail:note.text||null,
      sourceId:id,
      stageId:note.stageId||null,
      itemId:note.itemId||null,
      pending:pending(note),
      meta:{itemName:note.itemId?itemName(note.itemId):null}
    }));
  }

  const liveStartMs=actualStartAt?Date.parse(actualStartAt):null;
  for(const photo of rows(bundle.photos)){
    const timestamp=validTimestamp(photo?.capturedAt||photo?.createdAt);
    const id=String(photo?.id||'');
    if(!timestamp||!id)continue;
    if(liveStartMs!=null&&Date.parse(timestamp)<liveStartMs)continue;
    result.push(entry({
      key:'photo:'+id,
      type:LiveTimelineType.PHOTO,
      timestamp,
      label:'Zdjęcie',
      detail:photo.itemId?(itemName(photo.itemId)||null):null,
      sourceId:id,
      stageId:photo.stageId||null,
      itemId:photo.itemId||null,
      pending:pending(photo),
      meta:{photoStatus:photo.photoStatus||null}
    }));
  }

  const unique=new Map();
  for(const row of result.filter(Boolean)){
    if(!unique.has(row.key))unique.set(row.key,row);
  }

  return [...unique.values()].sort((a,b)=>
    Date.parse(a.timestamp)-Date.parse(b.timestamp)
    ||(TYPE_ORDER[a.type]??100)-(TYPE_ORDER[b.type]??100)
    ||a.key.localeCompare(b.key)
  );
}

export function createLiveTimeline({bundle={},limit=60}={}){
  const max=Number(limit);
  if(!Number.isInteger(max)||max<1||max>1000){
    throw new TypeError('timeline limit must be between 1 and 1000');
  }
  const all=timelineEntries(bundle);
  const start=Math.max(0,all.length-max);
  const visible=all.slice(start);
  return Object.freeze({
    total:all.length,
    visibleCount:visible.length,
    hiddenCount:start,
    hasEarlier:start>0,
    entries:Object.freeze(visible)
  });
}
